import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  GetEvidenceVersionOwnerDocument,
  type GetEvidenceVersionOwnerQuery,
  type GetEvidenceVersionOwnerQueryVariables,
} from "~/features/evidences/gqls";
import { useTenant } from "~/features/tenant/context";

/**
 * The canonical evidence owning a version row with this ID, for an ID that is no longer an evidence.
 * Cutover keeps each dropped evidence's ID as the ID of the version it became.
 */
export function useEvidenceVersionOwner(id: string, skip: boolean) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const args = useMemo(
    () => (skip ? skipToken : { variables: { tenantId, id } satisfies GetEvidenceVersionOwnerQueryVariables }),
    [skip, tenantId, id],
  );
  const { data } = useSuspenseQuery<GetEvidenceVersionOwnerQuery, GetEvidenceVersionOwnerQueryVariables>(
    GetEvidenceVersionOwnerDocument,
    args,
  );
  // A skipped suspense query still exposes its previous result, which must not outrank a live evidence row.
  return skip ? null : (data?.evidenceVersions[0] ?? null);
}
